import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutTemplate, Plus } from "lucide-react";
import { Button, ConfirmDialog, EmptyState, InlineMessage, Input } from "@nexus/ui";
import { SectionLayout } from "@/components/layout/SectionLayout";
import { ListPageLayout } from "@/components/layout/ListPageLayout";
import { LoadErrorMessage } from "@/components/LoadErrorMessage";
import { WORKSPACE_LABEL } from "@/config/uiConfig";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { useAppContext } from "@/contexts/AppContext";
import { useDashboards } from "@/hooks/dashboard/useDashboards";
import { useDashboardSort } from "@/hooks/dashboard/useDashboardSort";
import { useCreateDashboard } from "@/hooks/dashboard/useCreateDashboard";
import { useDeleteDashboard } from "@/hooks/dashboard/useDeleteDashboard";
import { useTemplateOperation } from "@/hooks/dashboard/useTemplateOperation";
import type { DashboardRecord } from "@/lib/dashboard/types";
import { DashboardResults } from "./components/DashboardResults";
import { DashboardTableSkeleton } from "./components/DashboardTable";
import { DashboardResourceTabs } from "./components/DashboardResourceTabs";
import { useDashboardsNav } from "./dashboardsNav";
import { TemplateSharingDialog } from "./components/TemplateSharingDialog";
import { nextUntitledName } from "./newDashboard";
import { useIsAdmin } from "@/hooks/useAccount";
import { useExportComposition } from "@/pages/admin/configuration/useExportComposition";
import { apiErrorMessage } from "@/pages/admin/useTenantAdmin";
import { ImportFromFileItem } from "./components/ImportFromFileItem";

export function templateEditorPath(id: string) {
  return `/workspace/dashboards/${encodeURIComponent(id)}/edit?kind=template`;
}

export default function DashboardTemplatesPage() {
  const navigate = useNavigate();
  const authorization = useAuthorization();
  const canAuthor = !authorization.isClientProfile && authorization.canAccessSection("dashboards");
  const sidebarItems = useDashboardsNav();
  const isAdmin = useIsAdmin();
  const exporting = useExportComposition();
  const { selectedTenantIds } = useAppContext();
  const { sort, order, onSort } = useDashboardSort("updated_at", "desc");
  const templates = useDashboards({ kind: "template", state: "all", sort, order });
  const create = useCreateDashboard();
  const remove = useDeleteDashboard();
  const instantiate = useTemplateOperation("instantiate");
  const copy = useTemplateOperation("copy-template");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<DashboardRecord | null>(null);
  const [shareTarget, setShareTarget] = useState<DashboardRecord | null>(null);
  const rows = (templates.data ?? []).filter(
    (template) =>
      (template.state !== "draft" || template.access?.owned) &&
      `${template.draft?.name ?? template.name} ${template.description ?? ""}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const pending = create.isPending || instantiate.isPending || copy.isPending;

  async function createTemplate() {
    if (!canAuthor || pending) return;
    try {
      const record = await create.mutateAsync({
        kind: "template",
        name: nextUntitledName(
          (templates.data ?? [])
            .filter((template) => template.access?.owned)
            .flatMap((template) => [template.name, template.draft?.name ?? template.name]),
          "Untitled template",
        ),
        slug: `template-${crypto.randomUUID()}`,
        state: "draft",
        config: { widgets: [] },
        order: 0,
      });
      navigate(templateEditorPath(record.id));
    } catch {
      return;
    }
  }

  async function useTemplate(template: DashboardRecord, asTemplate = false) {
    if (pending || !canAuthor) return;
    try {
      const record = await (asTemplate ? copy : instantiate).mutateAsync({
        id: template.id,
        catalog_tenant_ids: selectedTenantIds.map(Number),
      });
      navigate(
        asTemplate
          ? templateEditorPath(record.id)
          : `/workspace/dashboards/${record.id}/edit?context=1`,
      );
    } catch {
      return;
    }
  }

  return (
    <SectionLayout
      sectionTitle={WORKSPACE_LABEL}
      sidebarItems={sidebarItems}
      title="Templates"
      breadcrumbTrail={[
        { label: WORKSPACE_LABEL, href: "/workspace/dashboards" },
        { label: "Templates" },
      ]}
      actions={
        canAuthor ? (
          <Button variant="primary" disabled={pending} onClick={() => void createTemplate()}>
            <Plus className="size-4" />
            Create template
          </Button>
        ) : undefined
      }
      menuItems={canAuthor && isAdmin ? <ImportFromFileItem from="templates" /> : undefined}
    >
      <DashboardResourceTabs kind="template" />
      {shareTarget && (
        <TemplateSharingDialog template={shareTarget} onClose={() => setShareTarget(null)} />
      )}
      {!canAuthor ? (
        <InlineMessage tone="warning" title="Template authoring is not available">
          Your account does not have dashboard authoring access.
        </InlineMessage>
      ) : (
        <ListPageLayout
          toolbar={
            <Input
              aria-label="Search templates"
              placeholder="Search templates"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          }
        >
          {templates.isError && (
            <LoadErrorMessage
              title="Templates could not be loaded"
              onRetry={() => void templates.refetch()}
            />
          )}
          {templates.isLoading && <DashboardTableSkeleton />}
          {exporting.isError && (
            <InlineMessage tone="error" title="Template could not be exported">
              {apiErrorMessage(exporting.error)}
            </InlineMessage>
          )}
          {(create.isError || instantiate.isError || copy.isError) && (
            <InlineMessage tone="error" title="Template operation failed">
              {(create.error ?? instantiate.error ?? copy.error)?.message ?? "Please try again."}
            </InlineMessage>
          )}
          {!templates.isLoading && !templates.isError && rows.length === 0 && (
            <EmptyState
              icon={LayoutTemplate}
              title={search ? "No matching templates" : "No templates yet"}
              description={
                search
                  ? "Try another search."
                  : "Your private and shared templates will appear here."
              }
            />
          )}
          {rows.length > 0 && (
            <DashboardResults
              dashboards={rows}
              sort={sort}
              order={order}
              onSort={onSort}
              onOpen={(template) =>
                template.access?.editable
                  ? navigate(templateEditorPath(template.id))
                  : void useTemplate(template)
              }
              onEdit={(template) => navigate(templateEditorPath(template.id))}
              onDuplicate={(template) => void useTemplate(template, true)}
              onInstantiate={(template) => void useTemplate(template)}
              onShare={setShareTarget}
              onExport={
                isAdmin
                  ? (template) =>
                      exporting.mutate({ kind: "templates", id: template.id, slug: template.slug })
                  : undefined
              }
              onDelete={(template) => {
                remove.reset();
                setDeleteTarget(template);
              }}
            />
          )}
        </ListPageLayout>
      )}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete template?"
        description={deleteTarget?.name ?? ""}
        confirmLabel="Delete"
        tone="danger"
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await remove.mutateAsync(deleteTarget.id);
            setDeleteTarget(null);
          } catch {
            return;
          }
        }}
      >
        {remove.isError && (
          <InlineMessage tone="error" title="Template could not be deleted">
            Please try again.
          </InlineMessage>
        )}
      </ConfirmDialog>
    </SectionLayout>
  );
}
